/* 2026-06 — Model Roles.
 *
 * Two jobs, two models: one writes and edits the manuscript (drafting,
 * analysis, outlines, Read Manuscript → Fill Story Bible), another
 * answers in the Chat panel. Set per novel, optionally saved as the
 * default every new novel inherits.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { BookOpen, MessagesSquare, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { modelsApi, projectsApi } from '@/lib/api';

export default function ModelRolesDialog({ open, onOpenChange, projectId }) {
  const [models, setModels]   = useState([]);
  const [roles, setRoles]     = useState(null);
  const [manuscript, setManuscript] = useState('');
  const [chat, setChat]       = useState('');
  const [asDefault, setAsDefault] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [catalog, current] = await Promise.all([
        modelsApi.list(), projectsApi.modelRoles(projectId),
      ]);
      setModels(catalog.models || []);
      setRoles(current);
      setManuscript(current.manuscript_model_id || '');
      setChat(current.chat_model_id || '');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { if (open) load().catch(() => toast.error('Could not load model roles.')); }, [open, load]);

  const save = async () => {
    setSaving(true);
    const prevManuscript = roles?.manuscript_model_id;
    try {
      const payload = { save_as_account_default: asDefault };
      if (manuscript && manuscript !== roles?.manuscript_model_id) payload.manuscript_model_id = manuscript;
      if (chat && chat !== roles?.chat_model_id) payload.chat_model_id = chat;
      const r = await projectsApi.setModelRoles(projectId, payload);
      setRoles(r);
      window.dispatchEvent(new CustomEvent('model-roles-changed', { detail: r }));
      // Model switch warning — Anthropic prompt-cache keys are model-scoped.
      // Switching the manuscript model resets the 1-hour KV cache: the first
      // AI call with the new model is always a cache_write (2× base input cost),
      // not a cache_hit. Warn the writer so they understand the cost blip.
      if (payload.manuscript_model_id && prevManuscript && payload.manuscript_model_id !== prevManuscript) {
        toast.info('Manuscript model changed — prompt cache reset', {
          description: 'The first generation with the new model rebuilds the 1-hour cache (cache write). Subsequent calls will be cheaper.',
          duration: 7000,
        });
      } else {
        toast.success('Model roles saved.', {
          description: asDefault ? 'New novels will inherit this pairing.' : undefined,
        });
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save model roles.');
    } finally { setSaving(false); }
  };

  const picker = (value, onChange, testId) => {
    const selectedModel = models.find((m) => m.id === value);
    return (
      <div className="flex items-center gap-2">
        <Select value={value} onValueChange={onChange} disabled={loading}>
          <SelectTrigger className="h-9 flex-1 text-[12.5px]" data-testid={testId}>
            {loading
              ? <span className="flex items-center gap-1.5 text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</span>
              : <SelectValue placeholder="Choose a model" />}
          </SelectTrigger>
          <SelectContent>
            {models.map((m) => (
              <SelectItem key={m.id} value={m.id} className="text-[12.5px]">
                {m.label}{m.cost_tier ? ` · ${m.cost_tier}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedModel && (
          <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
              <button
                type="button"
                aria-label="Model information"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent className="w-64 text-[12px]" side="right" align="start">
              <p className="font-semibold text-[13px]">{selectedModel.label}</p>
              {selectedModel.recommended_for && (
                <p className="mt-1 text-muted-foreground">
                  <span className="font-medium text-foreground">Best for:</span> {selectedModel.recommended_for}
                </p>
              )}
              {selectedModel.cost_tier && (
                <p className="mt-0.5 text-muted-foreground">
                  <span className="font-medium text-foreground">Cost tier:</span> {selectedModel.cost_tier}
                </p>
              )}
            </HoverCardContent>
          </HoverCard>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="model-roles-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Model roles for this novel</DialogTitle>
          <DialogDescription className="text-[12.5px]">
            One model writes and edits the manuscript. A second, lighter one handles
            everyday chat. Either can be changed at any time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 text-[12px] font-semibold">
              <BookOpen className="h-3.5 w-3.5 text-primary" /> Manuscript
            </div>
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              Drafting, chapter &amp; novel analysis, outlines, bulk build, and
              Read Manuscript → Fill Story Bible.
            </p>
            {picker(manuscript, setManuscript, 'model-roles-manuscript')}
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 text-[12px] font-semibold">
              <MessagesSquare className="h-3.5 w-3.5 text-primary" /> Everyday chat
            </div>
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              The Chat with AI panel only. The picker in that panel still overrides
              this for a single conversation.
            </p>
            {picker(chat, setChat, 'model-roles-chat')}
          </div>

          <label className="flex items-start gap-2 text-[11.5px] leading-relaxed">
            <Checkbox
              checked={asDefault}
              onCheckedChange={(v) => setAsDefault(!!v)}
              data-testid="model-roles-as-default"
              className="mt-0.5"
            />
            <span>
              Save as my account default — every new novel starts with this pairing.
              {roles?.account_defaults && (
                <span className="block text-muted-foreground">
                  Current default: {roles.account_defaults.manuscript_model_id} ·{' '}
                  {roles.account_defaults.chat_model_id}
                </span>
              )}
            </span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} data-testid="model-roles-cancel">
            Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={saving || loading || !manuscript || !chat} data-testid="model-roles-save">
            {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Save roles
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
